import { downloadSheetLocally } from '@api/utils'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, Horizontal } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import type { GridApi } from 'ag-grid-community'
import type React from 'react'
import { useCallback, useMemo } from 'react'

type Props = {
  data: any
  api: GridApi
}

export const ModelGridActionButtons: React.FC<Props> = ({ data, api: gridApi }) => {
  const { scopes } = useSecurityContext()
  const queryClient = useQueryClient()

  const showModelDownload = useMemo(() => scopes?.model.download && data.has_solver_model, [scopes, data])
  const showResultDownload = useMemo(() => scopes?.model.download && data.has_result, [scopes, data])
  const showCancelButton = useMemo(() => scopes?.model.cancel && data.status === 'Running', [scopes, data])
  const showReRunButton = useMemo(
    () => data.can_rerun && scopes.model.download && scopes.scopes.includes('o.w'),
    [scopes, data]
  )

  const refreshView = () =>
    useCallback(() => {
      queryClient.cancelQueries({
        queryKey: ['modelHistoryOverviewQuery'],
      })
      queryClient
        .invalidateQueries({
          queryKey: ['modelHistoryOverviewQuery'],
        })
        .then(() => gridApi.refreshCells({ force: true }))
    }, [queryClient])

  const downloadResults = useCallback(
    () => api.downloadResults(data.id).then((resp) => downloadSheetLocally(resp, 'xlsx', 'results')),
    [data]
  )

  const downloadModel = useCallback(
    () => api.downloadTempModel(data.id).then((resp) => downloadSheetLocally(resp, 'xlsx', 'model')),
    [data]
  )
  const cancelModelRun = useCallback(() => api.killRun(data.id).then(refreshView), [data])
  const rerunModel = useCallback(() => api.reRunModel(data.id).then(refreshView), [data])

  return (
    <Horizontal verticalCenter className='h-100'>
      {showModelDownload && <GraviButton buttonText='Model' className='mr-2' onClick={downloadModel} />}
      {showResultDownload && <GraviButton buttonText='Results' className='mr-2' onClick={downloadResults} />}
      {showReRunButton && <GraviButton className='mr-2' onClick={rerunModel} theme2 buttonText='Rerun' />}
      {showCancelButton && <GraviButton onClick={cancelModelRun} error buttonText='Cancel' />}
    </Horizontal>
  )
}
